// Camera adapter — wrapper sopra @capacitor/camera per rispettare l'invariante #6
// del progetto: nessun import vendor SDK fuori da `lib/adapters/`. Tutte le pagine
// ora consumano questo file invece di importare direttamente Capacitor.

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { withOperatorExternalActivity } from '../../auth/operatorExternalActivity.js';

export type CaptureSource = 'camera' | 'gallery';
export interface CaptureOptions { quality?: number }

export interface CapturedPhoto {
  base64: string;
  mime: string; // es. 'image/jpeg'
}

export interface CaptureProvider {
  takePhoto(source: CaptureSource, options?: CaptureOptions): Promise<CapturedPhoto | null>;
  // T-170 1d: cattura multipla (pagine di un documento). Gallery → multi-select;
  // camera → singolo scatto (il tray accumula con chiamate ripetute).
  pickPhotos(source: CaptureSource, max: number, options?: CaptureOptions): Promise<CapturedPhoto[]>;
}

async function blobToBase64(blob: Blob): Promise<string> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error('read_failed'));
    r.readAsDataURL(blob);
  });
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

// Capacitor Android/iOS use this exact rejection for the user's cancel action.
function cancelledByUser(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'message' in error
    && error.message === 'User cancelled photos app';
}

class CapacitorCameraAdapter implements CaptureProvider {
  async takePhoto(source: CaptureSource, options?: CaptureOptions): Promise<CapturedPhoto | null> {
    const photo = await withOperatorExternalActivity(() => Camera.getPhoto({
      // T-407: qualità alzata 80→90 per etichette più nitide (numeri di lotto/
      // scadenza spesso in caratteri piccoli) → OCR più affidabile. Resta la
      // fotocamera NATIVA del telefono (intent di sistema) + piena risoluzione.
      quality: options?.quality ?? 90,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
      correctOrientation: true,
    })).catch((error: unknown) => {
      if (cancelledByUser(error)) return null;
      throw error;
    });
    if (!photo?.base64String) return null;
    return {
      base64: photo.base64String,
      mime: photo.format ? `image/${photo.format}` : 'image/jpeg',
    };
  }

  async pickPhotos(source: CaptureSource, max: number, options?: CaptureOptions): Promise<CapturedPhoto[]> {
    if (source === 'gallery') {
      try {
        const res = await withOperatorExternalActivity(
          () => Camera.pickImages({ quality: options?.quality ?? 90, limit: max }),
        );
        const out: CapturedPhoto[] = [];
        for (const ph of (res.photos ?? []).slice(0, max)) {
          const path = ph.webPath ?? ph.path;
          if (!path) continue;
          const resp = await fetch(path);
          const blob = await resp.blob();
          out.push({
            base64: await blobToBase64(blob),
            mime: blob.type || (ph.format ? `image/${ph.format}` : 'image/jpeg'),
          });
        }
        return out;
      } catch (error) {
        if (cancelledByUser(error)) return [];
        // fallback al singolo scatto sotto
      }
      const single = await this.takePhoto('gallery', options);
      return single ? [single] : [];
    }
    const single = await this.takePhoto('camera', options);
    return single ? [single] : [];
  }
}

// Web fallback: usa <input type=file capture=environment>.
// Più affidabile del modal pwa-elements su Chrome/Safari mobile (che resta
// "in caricamento" senza esito quando i custom elements non si registrano in tempo).
class WebFileInputAdapter implements CaptureProvider {
  async takePhoto(source: CaptureSource): Promise<CapturedPhoto | null> {
    return withOperatorExternalActivity(() => new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      if (source === 'camera') input.setAttribute('capture', 'environment');
      input.style.position = 'fixed';
      input.style.left = '-10000px';
      let settled = false;
      const cleanup = () => {
        if (input.parentNode) input.parentNode.removeChild(input);
      };
      input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!file) { settled = true; cleanup(); resolve(null); return; }
        const reader = new FileReader();
        reader.onload = () => {
          settled = true;
          cleanup();
          const result = reader.result as string;
          const comma = result.indexOf(',');
          const base64 = comma >= 0 ? result.slice(comma + 1) : result;
          resolve({ base64, mime: file.type || 'image/jpeg' });
        };
        reader.onerror = () => { settled = true; cleanup(); resolve(null); };
        reader.readAsDataURL(file);
      });
      // L'evento 'cancel' del file picker non è sempre supportato; meccanismo di sicurezza
      // basato su window focus per chiudere il loading se l'utente annulla.
      const onFocus = () => {
        setTimeout(() => {
          if (!settled && !input.files?.length) {
            cleanup();
            window.removeEventListener('focus', onFocus);
            resolve(null);
          }
        }, 500);
      };
      window.addEventListener('focus', onFocus, { once: true });
      document.body.appendChild(input);
      input.click();
    }));
  }

  async pickPhotos(source: CaptureSource, max: number): Promise<CapturedPhoto[]> {
    return withOperatorExternalActivity(() => new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      if (source === 'camera') input.setAttribute('capture', 'environment');
      input.style.position = 'fixed';
      input.style.left = '-10000px';
      let settled = false;
      const cleanup = () => {
        if (input.parentNode) input.parentNode.removeChild(input);
      };
      input.addEventListener('change', async () => {
        const files = Array.from(input.files ?? []).slice(0, max);
        if (files.length === 0) { settled = true; cleanup(); resolve([]); return; }
        const out: CapturedPhoto[] = [];
        for (const file of files) {
          try {
            out.push({ base64: await blobToBase64(file), mime: file.type || 'image/jpeg' });
          } catch {
            // skip file illeggibile
          }
        }
        settled = true;
        cleanup();
        resolve(out);
      });
      const onFocus = () => {
        setTimeout(() => {
          if (!settled && !input.files?.length) {
            cleanup();
            window.removeEventListener('focus', onFocus);
            resolve([]);
          }
        }, 500);
      };
      window.addEventListener('focus', onFocus, { once: true });
      document.body.appendChild(input);
      input.click();
    }));
  }
}

let instance: CaptureProvider | null = null;
export function getCaptureProvider(): CaptureProvider {
  if (!instance) {
    instance = Capacitor.getPlatform() === 'web'
      ? new WebFileInputAdapter()
      : new CapacitorCameraAdapter();
  }
  return instance;
}
